import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { mockUdemyCourses } from '../../data/mockCourses';
import { Course } from '../../types/course';
import { Loader2, AlertCircle, Check, Star, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CareerAnalysisData, safeParseCareerAnalysis } from '../../types/analysis';

interface CoursesSectionProps {
  analysis: CareerAnalysisData | null;
}

export default function CoursesSection({ analysis }: CoursesSectionProps) {
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [purchasedCourses, setPurchasedCourses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dynamicIntroText, setDynamicIntroText] = useState<string>('Rozwiń swoje umiejętności z naszymi polecanymi kursami!');

  const { user } = useAuthStore();
  const isLoggedIn = !!user;

  useEffect(() => {
    try {
      if (isLoggedIn && user?.id) {
        const storedPurchasedCourses = localStorage.getItem(`purchased_courses_${user.id}`);
        if (storedPurchasedCourses) {
          setPurchasedCourses(JSON.parse(storedPurchasedCourses));
        }
      }
    } catch (error) {
      console.error('Błąd podczas ładowania zakupionych kursów:', error);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    const currentAnalysis = analysis ? safeParseCareerAnalysis(analysis) : null;

    if (!currentAnalysis) {
      const bestsellers = mockUdemyCourses.filter(course => course.bestSeller).slice(0, 4);
      setRecommendedCourses(bestsellers);
      setDynamicIntroText('Odkryj nasze najpopularniejsze kursy i zdobądź nowe umiejętności!');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const keywords = new Set<string>();

      currentAnalysis.sciezkiKariery?.forEach(sciezka => {
        if (sciezka.nazwa) keywords.add(sciezka.nazwa.toLowerCase());
        if (sciezka.opis) {
          sciezka.opis.toLowerCase().split(/\s+/).forEach(word => {
            const cleanedWord = word.replace(/[^a-ząęółśżźćń]/gi, '');
            if (cleanedWord.length > 3) keywords.add(cleanedWord);
          });
        }
      });

      currentAnalysis.umiejetnosci?.forEach(umiejetnosc => {
        if (umiejetnosc.nazwa) keywords.add(umiejetnosc.nazwa.toLowerCase());
        if (umiejetnosc.opis) {
          umiejetnosc.opis.toLowerCase().split(/\s+/).forEach(word => {
            const cleanedWord = word.replace(/[^a-ząęółśżźćń]/gi, '');
            if (cleanedWord.length > 3) keywords.add(cleanedWord);
          });
        }
      });

      const quizAnswers = user ? localStorage.getItem(`quiz_${user.id}`) : null;
      if (quizAnswers) {
        try {
          const parsedAnswers = JSON.parse(quizAnswers);
          if (parsedAnswers.additional_interests) {
            parsedAnswers.additional_interests.toLowerCase().split(/[\,\s]+/).forEach((word: string) => {
              const cleanedWord = word.replace(/[^a-ząęółśżźćń]/gi, '');
              if (cleanedWord.length > 3) keywords.add(cleanedWord);
            });
          }
          if (parsedAnswers.additional_info) {
            parsedAnswers.additional_info.toLowerCase().split(/\s+/).forEach((word: string) => {
               const cleanedWord = word.replace(/[^a-ząęółśżźćń]/gi, '');
              if (cleanedWord.length > 3) keywords.add(cleanedWord);
            });
          }
        } catch (e) { console.error("Błąd parsowania odpowiedzi quizu dla słów kluczowych kursów", e); }
      }

      const analysisKeywords = Array.from(keywords);

      const scoredCourses = mockUdemyCourses.map(course => {
        let score = 0;
        const courseTitle = course.title?.toLowerCase() || '';
        const courseDesc = course.description?.toLowerCase() || '';
        const courseTags = course.tags?.join(' ').toLowerCase() || '';
        const courseCategory = course.category?.toLowerCase() || '';
        
        const courseText = `${courseTitle} ${courseDesc} ${courseTags}`;

        analysisKeywords.forEach(keyword => {
          if (courseText.includes(keyword)) {
            if (courseTags.includes(keyword) || courseTitle.includes(keyword)) {
              score += 3; 
            } else {
              score += 1;
            }
          }
        });

        currentAnalysis.sciezkiKariery?.forEach(sciezka => {
          if (sciezka.nazwa && courseCategory.includes(sciezka.nazwa.toLowerCase())) {
             score += 1;
          }
        });

        if (course.bestSeller) {
             score += 1;
        }

        return { course, score };
      });

      scoredCourses.sort((a, b) => b.score - a.score);
      const topCourses = scoredCourses.filter(item => item.score > 1).slice(0, 4).map(item => item.course);

      if (topCourses.length > 0) {
        setRecommendedCourses(topCourses);
        const topSkill = currentAnalysis.umiejetnosci?.[0]?.nazwa || 'kluczowych umiejętności';
        const topCareer = currentAnalysis.sciezkiKariery?.[0]?.nazwa || 'rekomendowanych ścieżek';
        if (currentAnalysis.sciezkiKariery && currentAnalysis.sciezkiKariery.length > 0) {
        setDynamicIntroText(`Twoja analiza wskazuje na potencjał w obszarze **${topCareer}**. Rozwiń **${topSkill}** i inne kompetencje dzięki tym specjalnie dobranym kursom, aby zmaksymalizować swoje szanse na sukces!`);
        } else {
          setDynamicIntroText(`Na podstawie Twoich zainteresowań i preferencji, te kursy pomogą Ci rozwinąć **${topSkill}** i inne cenne umiejętności:`);
        }
      } else {
        const bestsellers = mockUdemyCourses.filter(course => course.bestSeller).slice(0, 4);
        setRecommendedCourses(bestsellers);
        setDynamicIntroText('Odkryj nasze najpopularniejsze kursy i zdobądź nowe, cenne umiejętności:');
      }

    } catch (error) {
      console.error('Błąd podczas filtrowania kursów na podstawie analizy:', error);
      const bestsellers = mockUdemyCourses.filter(course => course.bestSeller).slice(0, 4);
      setRecommendedCourses(bestsellers);
      setDynamicIntroText('Wystąpił błąd podczas personalizacji. Sprawdź nasze najpopularniejsze kursy:');
    }

    setIsLoading(false);
  }, [analysis, user]);

  const isCourseAlreadyPurchased = (courseId: string): boolean => {
    return purchasedCourses.includes(courseId);
  };

  return (
    <div className="mt-12"> 
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Kursy dla Ciebie</h2>
      <p className="text-gray-600 mb-8" dangerouslySetInnerHTML={{ __html: dynamicIntroText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-purple-600">$1</strong>') }} />

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : recommendedCourses.length === 0 ? (
        <div className="text-center text-gray-500 py-10">
          <AlertCircle className="w-10 h-10 mx-auto mb-4 text-gray-400" />
          Nie znaleziono rekomendowanych kursów dla Twojego profilu.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {recommendedCourses.map((course) => (
            <Link key={course.id} to={`/kursy/${course.id}`} className="flex">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col border border-gray-100 hover:shadow-xl transition-shadow w-full"
              >
                <div className="relative">
                  <img 
                    src={course.imageUrl || `https://placehold.co/480x270/E9D5FF/4C1D95?text=${encodeURIComponent((course.title || 'Kurs').substring(0, 15))}...`} 
                    alt={course.title || 'Kurs'} 
                    className="w-full h-40 object-cover bg-purple-100"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = `https://placehold.co/480x270/E9D5FF/4C1D95?text=${encodeURIComponent((course.title || 'Kurs').substring(0, 15))}...`;
                    }}
                  />
                  {course.originalPrice && course.price < course.originalPrice && (
                    <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                      -{Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)}%
                    </span>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex-grow min-h-[3em]">{course.title || 'Bez tytułu'}</h3> 
                  <div className="flex items-center gap-1 text-yellow-500 mb-3">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm font-medium text-gray-700">{course.rating || 0}</span>
                    <span className="text-sm text-gray-500">({course.reviews || 0} opinii)</span> 
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-grow">{course.description || 'Brak opisu'}</p> 
                  <div className="flex flex-wrap gap-2 mb-4">
                    {course.tags?.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded font-medium">
                        {tag}
                      </span>
                    ))}
                    {(course.tags?.length || 0) > 3 && (
                       <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-medium">
                        +{(course.tags?.length || 0) - 3}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-auto pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center mb-3">
                      <span>{course.duration || 'Czas nieznany'}</span>
                      {course.textOnly && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">Tekstowy</span>
                      )}
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <div className="text-xl font-bold text-gray-900">
                        {course.price?.toFixed(2) || '0.00'} {course.currency || 'PLN'}
                        {course.originalPrice && (
                          <span className="text-sm text-gray-500 line-through ml-2">
                            {course.originalPrice.toFixed(2)} {course.currency || 'PLN'}
                          </span>
                        )}
                      </div>
                    </div>

                    {isCourseAlreadyPurchased(course.id) ? (
                      <button 
                        disabled 
                        className="w-full bg-green-100 text-green-700 py-2.5 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed"
                      >
                        <Check className="w-4 h-4"/> Zakupiono
                      </button>
                    ) : (
                      <button
                        
                        className="w-full bg-purple-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                      >
                        <BookOpen size={16} /> Zobacz kurs
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
